import React, { useMemo, useState } from "react";
import { PieChart } from "@mui/x-charts/PieChart";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import { useMediaQuery } from "@mui/material";
import { useSelector } from "react-redux";

import "./DataTagSensitiveRatio.scss";
import { percentageCalculation } from "../../../components/common/utilities";
import DashboardRegulatedScatter from "./DashboardRegulatedScatter";
import DashboardRegulatedPopup from "./DashboardRegulatedPopup";

export default function DataTagSensitiveRatio({ data }) {

  const isXL = useMediaQuery("(min-width:1800px)");
  const isMD = useMediaQuery("(min-width:1400px)");
  const isNormal = !isXL && !isMD;

  const total = useSelector((state) => state.dataTagReducer.totalFiles);
  const categoryWiseCounts = data?.categoryWiseCounts;

  const regulatedDataEmpty =
  Array.isArray(data?.regulatedDataCount) &&
  data.regulatedDataCount.length === 0;


  const HIGH = categoryWiseCounts?.HIGH || 0;
  const MEDIUM = categoryWiseCounts?.MEDIUM || 0;
  const LOW = categoryWiseCounts?.LOW || 0;
  const NOT_PROCESSED = categoryWiseCounts?.NOT_PROCESSED || 0;

  const sensitiveTotal = HIGH + MEDIUM + LOW;

  const highPct = percentageCalculation([{count: HIGH}], total)[0]?.count || 0;
  const medPct = percentageCalculation([{count: MEDIUM}], total)[0]?.count || 0;
  const lowPct = percentageCalculation([{count: LOW}], total)[0]?.count || 0;
  const notProcessed = percentageCalculation([{count: NOT_PROCESSED}], total)[0]?.count || 0;

  const [showSensitive, setShowSensitive] = useState(regulatedDataEmpty);

  const centerPct = Math.round(
    percentageCalculation([{ count: sensitiveTotal }], total)[0]?.count || 0
  );

  const chartSize = useMemo(() => {
    if (isXL) return { width: 320, height: 200 };
    if (isMD) return { width: 280, height: 180 };
    return { width: 390, height: 250 };
  }, [isXL, isMD, isNormal]);

  const pieData = [
    { id: 0, value: HIGH, color: "#f44336", label: "High" },
    { id: 1, value: MEDIUM, color: "#ff9800", label: "Medium" },
    { id: 2, value: LOW, color: "#2196f3", label: "Low" },
    { id: 3, value: NOT_PROCESSED, color: "#9e9e9e", label: "Not Processed" },
  ];

  return (
    <div className="data-tag-sensitive-ratio">
      <div className={showSensitive ? "ratio-header sensitive" : "ratio-header"}>
        <div className="ratio-title-wrapper">
          <h4 className="ratio-title">
            {showSensitive ? "Sensitive Data" : "Regulated Data"}
          </h4>

          {!showSensitive && (
            <DashboardRegulatedPopup
              data={data?.regulatedDataCount}
            />
          )}
        </div>

        <FormControlLabel
          className="regulated-switch"
          control={
            <Switch
              size="small"
              checked={showSensitive}
              onChange={() => setShowSensitive((v) => !v)}
            />
          }
          label=""
        />
      </div>

      <div className="chart-row">
        {showSensitive ? (
          <>
            <div className="chart-wrapper">
              <PieChart
                width={chartSize.width}
                height={chartSize.height}
                series={[
                  {
                    data: pieData,
                    innerRadius: 55,
                    outerRadius: 70,
                    cornerRadius: 3,
                    paddingAngle: 1,
                  },
                ]}
                slotProps={{ legend: { hidden: true } }}
              />
              <div className="center-percent">{centerPct}%</div>
            </div>

            <div className="custom-legend">
              <div className="legend-item sensitivity">
                <span className="dot high" /> High ({highPct}%)
              </div>
              <div className="legend-item sensitivity">
                <span className="dot medium" /> Medium ({medPct}%)
              </div>
              <div className="legend-item sensitivity">
                <span className="dot low" /> Low ({lowPct}%)
              </div>
              <div className="legend-item sensitivity">
                <span className="dot not-processed" /> Not Processed (
                {notProcessed}%)
              </div>
            </div>
          </>
        ) : (
          <DashboardRegulatedScatter data={data?.regulatedDataCount} />
        )}
      </div>
    </div>
  );
}
